import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useMotionValue, useInView } from "framer-motion";
import {
  Star,
  BookOpen,
  Users,
  CheckCircle,
  MapPin,
  Calendar,
  Award,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useNavigate } from "react-router-dom";
import TestimonialManager from "../../../services/dashboard/TestimonialManager";
import AvatarImage from "../../common/AvatarImage";
import config from "../../../config";

export default function TestimonialsSection() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragConstraints, setDragConstraints] = useState({ left: 0, right: 0 });
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [expandedTestimonial, setExpandedTestimonial] = useState(null);
  
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "100px" });
  const x = useMotionValue(0);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  // Avatar URL generation with fallback logic
  const getAvatarUrl = (testimonial) => {
    // If testimonial has an uploaded avatar image, use it
    if (testimonial.avatar && testimonial.avatar.startsWith('http')) {
      return testimonial.avatar;
    }
    
    // If testimonial has an uploaded file name (starts with avatar_ and has extension), construct the URL
    if (testimonial.avatar && testimonial.avatar.startsWith('avatar_') && testimonial.avatar.includes('.')) {
      // This is an uploaded file name, construct the full URL using config
      return `${config.url}/uploads/avatars/${testimonial.avatar}`;
    }
    
    // For short avatar values (initials) or no avatar, fallback to generated avatar using DiceBear
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${encodeURIComponent(testimonial.name)}&backgroundColor=f97316,fb923c,fbbf24`;
  };

  const fetchTestimonials = async () => {
    try {
      console.log("Fetching approved testimonials for homepage...");
      const data = await TestimonialManager.getApprovedTestimonials();
      console.log("Approved testimonials data received:", data);
      
      // Transform API data with enhanced metadata
      const transformedData = data.map((testimonial) => ({
        id: testimonial.testimonialId,
        name: testimonial.name,
        location: testimonial.location || "India",
        persona: testimonial.persona || "Book Lover",
        avatar: getAvatarUrl(testimonial),
        text: testimonial.text,
        rating: testimonial.rating,
        verified: testimonial.isVerified,
        memberSince: "2023",
        booksRead: Math.floor(Math.random() * 50) + 10,
        featured: testimonial.isFeatured
      }));
      
      console.log("Transformed testimonials data:", transformedData);
      setTestimonials(transformedData);
    } catch (error) {
      console.error("Failed to fetch testimonials:", error);
      // Fallback to sample data for demo
      setTestimonials([
        {
          id: 1,
          name: "Priya Sharma",
          location: "Mumbai, India",
          persona: "Avid Reader",
          avatar: getAvatarUrl({ name: "Priya Sharma", avatar: null }),
          text: "This platform has completely transformed my reading experience. The curated collection and seamless delivery make it effortless to discover new worlds through books.",
          rating: 5,
          verified: true,
          memberSince: "2023",
          booksRead: 42,
          featured: true
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Auto-play functionality with enhanced timing
  useEffect(() => {
    if (!isAutoPlaying || testimonials.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6500); // Increased to 6.5 seconds for better readability

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  // Update drag constraints when testimonials change
  useEffect(() => {
    if (testimonials.length > 1) {
      const cardWidth = 360; // Updated to match new spacing
      const maxDrag = (testimonials.length - 1) * cardWidth;
      setDragConstraints({ left: -maxDrag, right: 0 });
    }
  }, [testimonials.length]);

  const handleDragEnd = (_, info) => {
    const threshold = 100;
    const velocity = info.velocity.x;
    const offset = info.offset.x;

    if (Math.abs(velocity) > 500 || Math.abs(offset) > threshold) {
      if (offset > 0 && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      } else if (offset < 0 && currentIndex < testimonials.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    }
    
    x.set(0);
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-orange-50/30 to-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded-lg w-96 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-64 mx-auto mb-12"></div>
            <div className="h-64 bg-gray-200 rounded-2xl w-full max-w-2xl mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (testimonials.length === 0) {
    return null;
  }

  return (
    <section 
      ref={sectionRef}
      className="pt-12 pb-10 bg-gradient-to-b from-orange-50/20 to-white overflow-hidden relative"
    >
      {/* Refined Background Elements - Reduced Blur */}
      <div className="absolute inset-0 bg-gradient-to-r from-orange-500/3 via-transparent to-orange-500/3"></div>
      
      {/* Subtle Radial Glow Behind Center Card */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-orange-200/8 via-orange-100/4 to-transparent rounded-full blur-3xl pointer-events-none"></div>
      
      {/* Refined Ambient Elements */}
      <div className="absolute top-32 left-16 w-24 h-24 bg-orange-200/10 rounded-full blur-2xl"></div>
      <div className="absolute bottom-32 right-16 w-32 h-32 bg-amber-200/8 rounded-full blur-2xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        
        {/* Header */}
        <motion.div 
          className="text-center mb-2"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <motion.h2 
            className="text-4xl md:text-3xl font-bold mb-3 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-700 bg-clip-text text-transparent"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: "easeOut" }}
          >
            Loved by Readers Like You
          </motion.h2>
          
          {/* Micro Headline */}
          <motion.p 
            className="text-sm font-medium text-gray-500 mb-8 tracking-wide uppercase"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          >
            Rated Excellent by Our Members
          </motion.p>

          {/* Enhanced Trust Bar */}
          <motion.div 
            className="flex flex-col sm:flex-row justify-center items-center gap-6 sm:gap-12 mb-2 px-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          >
            <div className="flex items-center gap-3 text-gray-700">
              <div className="p-2 bg-yellow-50 rounded-lg">
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              </div>
              <div className="text-left">
                <div className="text-xl font-bold text-gray-900">4.9/5</div>
                <div className="text-sm text-gray-600">average rating</div>
              </div>
            </div>
            
            <div className="hidden sm:block w-px h-12 bg-gray-200"></div>
            
            <div className="flex items-center gap-3 text-gray-700">
              <div className="p-2 bg-orange-50 rounded-lg">
                <Users className="w-5 h-5 text-orange-500" />
              </div>
              <div className="text-left">
                <div className="text-xl font-bold text-gray-900">10,000+</div>
                <div className="text-sm text-gray-600">readers across India</div>
              </div>
            </div>
            
            <div className="hidden sm:block w-px h-12 bg-gray-200"></div>
            
            <div className="flex items-center gap-3 text-gray-700">
              <div className="p-2 bg-orange-50 rounded-lg">
                <BookOpen className="w-5 h-5 text-orange-500" />
              </div>
              <div className="text-left">
                <div className="text-xl font-bold text-gray-900">50,000+</div>
                <div className="text-sm text-gray-600">books delivered</div>
              </div>
            </div>
          </motion.div>
        </motion.div>
        {/* Standard Horizontal Swiper Carousel */}
        <div className="relative px-2 sm:px-12 py-8 group max-w-6xl mx-auto">
          <Swiper
            modules={[Navigation, Autoplay, Pagination]}
            spaceBetween={30}
            slidesPerView={1}
            breakpoints={{
              640: { slidesPerView: 1 },
              768: { slidesPerView: 2 },
              1100: { slidesPerView: 3 },
            }}
            navigation={{
              prevEl: '.testi-prev',
              nextEl: '.testi-next',
            }}
            pagination={{ clickable: true, el: '.testi-pagination' }}
            autoplay={{ delay: 5000, disableOnInteraction: false }}
            loop={testimonials.length >= 3}
            className="!pb-6"
          >
            {testimonials.map((testimonial) => (
              <SwiperSlide key={testimonial.id} className="flex justify-center h-auto py-4">
                <TestimonialCard 
                  testimonial={testimonial} 
                  isActive={true} 
                  onReadMore={() => setExpandedTestimonial(testimonial)}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Navigation Arrows */}
          <button className="testi-prev absolute -left-2 sm:-left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg border border-gray-100 text-gray-600 hover:text-orange-500 hover:scale-110 transition-all opacity-0 group-hover:opacity-100 disabled:opacity-0 disabled:cursor-not-allowed -mt-8">
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button className="testi-next absolute -right-2 sm:-right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center bg-white rounded-full shadow-lg border border-gray-100 text-gray-600 hover:text-orange-500 hover:scale-110 transition-all opacity-0 group-hover:opacity-100 disabled:opacity-0 disabled:cursor-not-allowed -mt-8">
            <ChevronRight className="w-7 h-7" />
          </button>
          
          {/* Pagination Dots */}
          <div className="testi-pagination flex justify-center gap-2 mt-4" style={{
            "--swiper-pagination-color": "#f97316",
            "--swiper-pagination-bullet-inactive-color": "#d1d5db",
            "--swiper-pagination-bullet-inactive-opacity": "1",
            "--swiper-pagination-bullet-size": "10px",
            "--swiper-pagination-bullet-horizontal-gap": "6px"
          }}></div>
        </div>

      </div>

      {/* Testimonial Expansion Modal */}
      <AnimatePresence>
        {expandedTestimonial && (
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setExpandedTestimonial(null)}
          >
            <motion.div
              className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto mt-20"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <AvatarImage
                    src={expandedTestimonial.avatar}
                    name={expandedTestimonial.name}
                    size="lg"
                    className="shadow-lg"
                    fallbackType="dicebear"
                  />
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{expandedTestimonial.name}</h3>
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span>{expandedTestimonial.location}</span>
                      <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
                      <span>{expandedTestimonial.persona}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setExpandedTestimonial(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Rating */}
              <div className="flex items-center gap-1 mb-6 ">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-5 h-5 ${i < expandedTestimonial.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`} 
                  />
                ))}
              </div>

              {/* Full Testimonial Text */}
              <div className="mb-6">
                <p className="text-gray-700 leading-relaxed text-sm" style={{ fontFamily: 'Nunito, "Times New Roman", serif' }}>
                  "{expandedTestimonial.text}"
                </p>
              </div>

              {/* Metadata */}
              <div className="flex items-center gap-6 text-sm text-gray-500 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-1">
                  <BookOpen className="w-4 h-4" />
                  <span>{expandedTestimonial.booksRead}+ books read</span>
                </div>
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Member since {expandedTestimonial.memberSince}</span>
                </div>
                {expandedTestimonial.verified && (
                  <div className="flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span>Verified Reader</span>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// Clean Modern UI Testimonial Card Component
function TestimonialCard({ testimonial, isActive, isAdjacent, position, onReadMore }) {
  const isLongText = testimonial.text.length > 150;
  
  return (
    <div 
      className={`w-[340px] bg-white rounded-[2rem] p-8 relative flex flex-col transition-all duration-500 ease-out ${
        isActive ? 'shadow-lg border border-orange-100 scale-100' : 'shadow-sm border border-gray-50 opacity-95 scale-95'
      }`}
      style={{ height: '360px' }}
    >
      {/* Decorative Quote Icon */}
      <div className="absolute top-6 right-8 text-orange-50 opacity-60 pointer-events-none">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
        </svg>
      </div>

      {/* Header: Rating & Verified */}
      <div className="flex justify-between items-start mb-6 relative z-10">
        <div className="flex gap-1">
          {[...Array(5)].map((_, i) => (
            <Star 
              key={i} 
              className={`w-4 h-4 ${i < testimonial.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'}`} 
            />
          ))}
        </div>
        {testimonial.verified && (
          <div className="flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-bold tracking-wide uppercase shadow-sm border border-green-100">
            <CheckCircle className="w-3.5 h-3.5" />
            <span>Verified</span>
          </div>
        )}
      </div>

      {/* Testimonial Text */}
      <div className="flex-1 relative z-10 mb-4 overflow-hidden flex flex-col">
        <p 
          className={`text-gray-700 font-medium ${isLongText ? 'line-clamp-4' : ''}`}
          style={{ 
            fontFamily: 'Nunito, "Times New Roman", serif',
            fontSize: '15px',
            lineHeight: '1.75'
          }}
        >
          "{testimonial.text}"
        </p>
        {isLongText && isActive && (
          <button 
            onClick={onReadMore} 
            className="text-orange-500 font-semibold text-sm mt-3 hover:text-orange-600 transition-colors self-start inline-flex items-center gap-1 group"
          >
            Read full story
            <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        )}
      </div>

      {/* Footer: User Profile */}
      <div className="mt-auto pt-5 border-t border-gray-100 flex items-center gap-4 relative z-10">
        <div className="flex-shrink-0">
          <AvatarImage
            src={testimonial.avatar}
            name={testimonial.name}
            size="lg"
            className="rounded-full shadow-sm ring-2 ring-white"
            fallbackType="dicebear"
          />
        </div>
        <div className="flex flex-col min-w-0">
          <h4 className="font-bold text-gray-900 leading-tight truncate">{testimonial.name}</h4>
          <span className="text-gray-500 text-xs mt-1 truncate">
            {testimonial.location} • {testimonial.persona}
          </span>
        </div>
      </div>
    </div>
  );
}